import {unwrapResult} from '@reduxjs/toolkit';
import {
    Button,
    Checkbox,
    Form,
    Input,
    Spin,
} from 'antd';
import {useForm} from 'antd/es/form/Form';
import cn from 'classnames';
import {pick} from 'lodash-es';
import React, {
    FC,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import {useHistory} from 'react-router-dom';

import {FieldTooltip} from 'components/form/field-tooltip';
import {CustomSelect} from 'components/form/inputs/custom-select';
import {
    ECustomSelectValueTriggerEvent,
} from 'components/form/inputs/custom-select/custom-select';
import {LargeStringInput} from 'components/form/inputs/large-string-input';
import {useEnableDynamicSql} from 'components/report-configuration/hooks/use-enable-dynamic-sql';
import {useFormFieldsManager, EWatcherFieldFlag} from 'components/report-configuration/hooks/use-form-fields-manager';
import {
    REPORT_CONFIGURATION_NAME,
    EReportConfigurationTabKey,
} from 'components/report-configuration/report-configuration.constants';
import {ReportConfigurationContext} from 'components/report-configuration/report-configuration.context';
import {ThunkErrorScreen} from 'components/thunk-error-screen';
import {IFieldTooltipMetadata} from 'entities/metadata/tooltip-metadata-types';
import {ModalReportActionsSettings} from 'features/configure-table-report-actions-menu';
import {EErrorMessages} from 'shared/constants/messages';
import {setFieldsValueUntouched, showMessage} from 'shared/utils';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';
import {
    selectThunkStatus,
} from 'store/slices/loading-state-slice/loading-state-slice';
import {TReportConfigurationDataConverted} from 'store/slices/report-configuration-slice';
import {
    loadTableReportConfiguration,
} from 'store/slices/report-configuration-slice/report-configuration';
import {
    reportConfigurationActions,
    selectTableReportConfigurationData,
} from 'store/slices/report-configuration-slice/report-configuration-slice';
import {
    TReportConfiguratorMainFields,
    reportConfiguratorMainFields,
} from 'store/slices/report-configuration-slice/report-configuration/report-configuration-constants';
import {
    createTableReportConfiguration,
    updateTableReportConfiguration,
} from 'store/slices/report-configuration-slice/report-configuration/report-configuration-thunks';

import {ButtonsBar} from '../../components/buttons-bar';
import {ClickConfirmation} from '../../components/click-confirmation';
import {isViewModeSwitchSupported} from '../report-table-settings/utils/report-table-settings.utils';
import {ModalReportDownloadSettings} from './download-settings-modal-opener';
import {createReportConfiguratorWatchers} from './report-configurator-watchers';
import {reportConfiguratorFetchMapping, switchTabConfirmationLabels} from './report-configurator.constants';
import {TableReservedColumnsModalOpener} from './table-reserved-columns-modal-opener';
import './report-configurator.less';

interface IReportConfiguratorProps {}

export const ReportConfigurator: FC<IReportConfiguratorProps> = () => {
    const [form] = useForm();
    const history = useHistory();
    const dispatch = useAppDispatch();

    const {
        setSelectedTabKey,
        entityName,
        templateCode,
        isCreatingNewTemplate,
    } = useContext(ReportConfigurationContext);

    const {setReportConfigurationTemplateBlocks} = reportConfigurationActions;

    const [isEditingForm, setIsEditingForm] = useState(isCreatingNewTemplate);
    const [chosenDesign, setChosenDesign] = useState<string|undefined>();

    const [valuesReferenceUrls, setValuesReferenceUrls] = useState<{ [index: string]: string | undefined }>({
        headerDatasourceValue: undefined,
        tableDatasourceValue: undefined,
    });

    const [shouldUpdateSelectUrlsTrigger, setShouldUpdateSelectUrlsTrigger] = useState(0);

    const shouldSetFirstValueToHeaderValueRef = useRef<boolean>(false);
    const shouldSetFirstValueToTableValueRef = useRef<boolean>(false);

    const reportConfigurationData = useAppSelector(selectTableReportConfigurationData);

    const isDynamicSqlEnabled = useEnableDynamicSql();

    const {checkWatcherFlag, applyFieldWatchers} = useFormFieldsManager({
        formInstance: form,
        watchers: createReportConfiguratorWatchers({
            isEditingForm,
            valuesReferenceUrls,
        }),
        triggerDeps: [isEditingForm, valuesReferenceUrls],
    });

    const [formInitialValues, setFormInitialValues] = useState<
        Pick<TReportConfigurationDataConverted, TReportConfiguratorMainFields> | undefined
    >(pick(reportConfigurationData, reportConfiguratorMainFields));

    useEffect(() => {
        if (reportConfigurationData) {
            const dataToSetToForm = pick(reportConfigurationData, reportConfiguratorMainFields);
            setFormInitialValues(dataToSetToForm);
            form.setFieldsValue(dataToSetToForm);
        }
    }, [reportConfigurationData]);

    useEffect(() => {
        if (formInitialValues) {
            form.resetFields();
            applyFieldWatchers();
            setShouldUpdateSelectUrlsTrigger(p => p + 1);
        }
    }, [formInitialValues]);

    const {
        isRejected: isLoadingReportConfigurationFailed,
        error: reportConfigurationLoadingError,
    } = useAppSelector(s => selectThunkStatus(s, loadTableReportConfiguration.typePrefix));

    const {
        isPending: isUpdatingReportConfiguration,
    } = useAppSelector(s => selectThunkStatus(s, updateTableReportConfiguration.typePrefix));

    const handleFinish = (values: Pick<TReportConfigurationDataConverted, TReportConfiguratorMainFields>) => {
        if (!templateCode) return;

        if (isCreatingNewTemplate) {
            const {templateCode: newTemplateCode} = values;
            dispatch(createTableReportConfiguration({
                data: values,
            }))
                .then(unwrapResult)
                .then(() => {
                    history.replace(`/${REPORT_CONFIGURATION_NAME}?entity=${newTemplateCode}`);
                    showMessage({message: `Шаблон «${newTemplateCode}» успешно создан`});
                }, error => {
                    showMessage({
                        message: error?.response?.data ?? EErrorMessages.UNEXPECTED_ERROR_OCCURRED,
                        isError: true,
                    });
                });
            return;
        }

        setIsEditingForm(false);
        dispatch(updateTableReportConfiguration({
            templateCode,
            data: values,
        }))
            .then(unwrapResult)
            .then(() => {
                dispatch(loadTableReportConfiguration({
                    templateCode,
                }));
            }, error => {
                showMessage({
                    message: error?.response?.data ?? EErrorMessages.UNEXPECTED_ERROR_OCCURRED,
                    isError: true,
                });
                setIsEditingForm(true);
            });
    };

    const getLeaveTabConfirmationSkipCondition = () => !form.isFieldsTouched();

    if (!isCreatingNewTemplate && isLoadingReportConfigurationFailed) {
        return (
            <ThunkErrorScreen
                error={reportConfigurationLoadingError}
            />
        );
    }

    return (
        <>
            <Spin
                spinning={false}
            >
                <section className={cn('report-configurator')}>
                    <ButtonsBar
                        entityName={entityName}
                        templateCode={templateCode}
                        isCreating={isCreatingNewTemplate}
                        isCreatingDisabled={isUpdatingReportConfiguration
                    || (!isCreatingNewTemplate && (!isEditingForm
                        || (!reportConfigurationData && !isCreatingNewTemplate)))}
                        isEditing={isEditingForm}
                        setIsEditing={setIsEditingForm}
                        titleBack="Назад"
                        isEditingDisabled={!reportConfigurationData && !isCreatingNewTemplate}
                        onSubmit={() => {
                            form.submit();
                        }}
                        onCancelEdit={() => {
                            form.resetFields();
                            if (!isCreatingNewTemplate) { setIsEditingForm(false); }
                        }}
                        onBack={() => history.push(REPORT_CONFIGURATION_NAME)}
                        showOnBackModalConfirmation={form.isFieldsTouched()}
                    />

                    <Form<Pick<TReportConfigurationDataConverted, TReportConfiguratorMainFields>>
                        form={form}
                        initialValues={formInitialValues}
                        onFinish={handleFinish}
                        layout="vertical"
                        onValuesChange={() => {
                            applyFieldWatchers();
                        }}
                    >
                        <article className={cn('report-configurator__section')}>
                            <h3 className={cn('report-configurator__section__title')}>
                                Основные параметры
                            </h3>

                            <div className={cn('report-configurator__section__body')}>
                                <div className={cn(
                                    'report-configurator__section__body__row',
                                )}
                                >
                                    <Form.Item
                                        style={{width: 555}}
                                        name="templateName"
                                        label="Наименование шаблона"
                                        rules={[
                                            {required: true},
                                            {max: 1000, message: 'Длина должна быть не более 1000 символов'},

                                        ]}
                                    >
                                        <Input
                                            disabled={checkWatcherFlag('templateName', EWatcherFieldFlag.isDisabled)}
                                            placeholder="Наименование шаблона"
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        style={{width: 160}}
                                        name="templateCode"
                                        label="Код"
                                        rules={[
                                            {required: true},
                                            {max: 100, message: 'Длина должна быть не более 100 символов'},
                                        ]}
                                    >
                                        <Input
                                            disabled={!isCreatingNewTemplate
                                            || checkWatcherFlag('templateCode', EWatcherFieldFlag.isDisabled)}
                                            placeholder="Код"
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        style={{width: 555}}
                                        name="reportName"
                                        label="Наименование формы отчетности"
                                        rules={[
                                            {required: true},
                                            {max: 100, message: 'Длина должна быть не более 100 символов'},
                                        ]}
                                    >
                                        <Input
                                            disabled={checkWatcherFlag('reportName', EWatcherFieldFlag.isDisabled)}
                                            placeholder="Наименование формы отчетности"
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        style={{width: 555}}
                                        name="designTemplate"
                                        label="Выбор макета"
                                        rules={[{required: true}]}
                                    >
                                        <CustomSelect
                                            settings={{
                                                showSearch: true,
                                                isDisabled: checkWatcherFlag('designTemplate',
                                                    EWatcherFieldFlag.isDisabled),
                                                placeholder: 'Выберите макет',
                                                url: '/lookupValue/TABLE_REPORT_DESIGN_TEMPLATE',
                                                labelPath: 'meaning',
                                                valuePath: 'lookupCode',
                                                formFieldKey: 'designTemplate',
                                                formInstance: form,
                                                triggerOnValueSet: true,
                                            }}
                                            handleTriggerEvent={(val, event) => {
                                                setChosenDesign(val?.lookupCode);
                                                if (event === ECustomSelectValueTriggerEvent.valueHasBeenChangedManually
                                                    || event === ECustomSelectValueTriggerEvent.valueWasSet) {
                                                    dispatch(setReportConfigurationTemplateBlocks(
                                                        Number(val?.attribute1),
                                                    ));
                                                }
                                            }}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        style={{width: 160}}
                                        name="paginationPosition"
                                        label="Позиция пагинации"
                                        initialValue="TOP"
                                    >
                                        <CustomSelect
                                            settings={{
                                                showSearch: true,
                                                isDisabled: checkWatcherFlag(
                                                    'paginationPosition', EWatcherFieldFlag.isDisabled,
                                                ),
                                                placeholder: 'Выберите позицию',
                                                url: '/lookupValue/TABLE_REPORT_PAGINATION_POSITION',
                                                labelPath: 'meaning',
                                                valuePath: 'lookupCode',
                                            }}
                                        />
                                    </Form.Item>
                                </div>

                                <div className={cn('report-configurator__section__body__row')}>
                                    <Form.Item
                                        name="enabledMenu"
                                        valuePropName="checked"
                                        className="with-tooltip"
                                        extra={!reportConfigurationData?.reservedColumns?.sheetCode && (
                                            <FieldTooltip
                                                meta={{
                                                    contentStructure: 'Не заполнено поле '
                                                        + '«Столбец для определения раздела»',
                                                } as IFieldTooltipMetadata}
                                            />
                                        )}
                                    >
                                        <Checkbox
                                            disabled={
                                                checkWatcherFlag('enabledMenu', EWatcherFieldFlag.isDisabled)
                                            || !reportConfigurationData?.reservedColumns?.sheetCode
                                            }
                                        >
                                            Использовать оглавление
                                        </Checkbox>
                                    </Form.Item>

                                    <Form.Item
                                        name="withDocumentId"
                                        valuePropName="checked"
                                    >
                                        <Checkbox disabled={checkWatcherFlag(
                                            'withDocumentId', EWatcherFieldFlag.isDisabled,
                                        )}
                                        >
                                            Связь с документом
                                        </Checkbox>
                                    </Form.Item>
                                </div>

                                {!isCreatingNewTemplate && (
                                    <footer className={cn('report-configurator__section__body__row')}>
                                        <ClickConfirmation
                                            skip={getLeaveTabConfirmationSkipCondition}
                                            labels={switchTabConfirmationLabels}
                                            mainTextCenter
                                        >
                                            <Button
                                                type="primary"
                                                disabled={isCreatingNewTemplate}
                                                onClick={() => {
                                                    setSelectedTabKey?.(
                                                        EReportConfigurationTabKey.REPORT_PROPERTIES_SETTINGS,
                                                    );
                                                }}
                                            >
                                                Параметры отчёта
                                            </Button>
                                        </ClickConfirmation>

                                        <ClickConfirmation
                                            skip={getLeaveTabConfirmationSkipCondition}
                                            labels={switchTabConfirmationLabels}
                                            mainTextCenter
                                        >
                                            <Button
                                                type="primary"
                                                onClick={() => {
                                                    setSelectedTabKey?.(
                                                        EReportConfigurationTabKey.REPORT_TABLE_OF_CONTENTS,
                                                    );
                                                }}
                                                disabled={
                                                    !form.getFieldValue('enabledMenu')
                                          || isCreatingNewTemplate
                                          || checkWatcherFlag('buttonHeaderSettings',
                                              EWatcherFieldFlag.isDisabled)
                                                }
                                            >
                                                Настройка оглавления
                                            </Button>
                                        </ClickConfirmation>
                                    </footer>
                                )}
                            </div>
                        </article>

                        <article className={cn('report-configurator__section')}>
                            <h3 className={cn('report-configurator__section__title')}>
                                Источник данных заголовка отчёта
                            </h3>

                            <div className={cn('report-configurator__section__body')}>
                                <div
                                    className={cn(
                                        'report-configurator__section__body__row',
                                    )}
                                >
                                    <Form.Item
                                        style={{width: 555}}
                                        name="headerDatasourceType"
                                        label="Тип источника"
                                    >
                                        <CustomSelect
                                            settings={{
                                                showSearch: true,
                                                isDisabled: checkWatcherFlag('headerDatasourceType',
                                                    EWatcherFieldFlag.isDisabled),
                                                placeholder: 'Выберите тип',
                                                url: '/lookupValue/TABLE_REPORT_SOURCE_TYPE',
                                                labelPath: 'meaning',
                                                valuePath: 'lookupCode',
                                                formFieldKey: 'headerDatasourceType',
                                                depsToCallCustomTrigger: [shouldUpdateSelectUrlsTrigger],
                                            }}
                                            handleTriggerEvent={(val, event) => {
                                                if (val?.lookupCode && val?.attribute1) {
                                                    const fetchUrl = `${reportConfiguratorFetchMapping[
                                                        val.lookupCode]}/${val.attribute1}`;
                                                    setValuesReferenceUrls(p => {
                                                        if (p.headerDatasourceValue === fetchUrl) return p;
                                                        if (event === ECustomSelectValueTriggerEvent
                                                            .valueHasBeenChangedManually) {
                                                            shouldSetFirstValueToHeaderValueRef.current = true;
                                                        }
                                                        return {
                                                            ...p,
                                                            headerDatasourceValue: fetchUrl,
                                                        };
                                                    });
                                                }
                                            }}
                                        />
                                    </Form.Item>

                                    {!checkWatcherFlag('headerDatasourceValue', EWatcherFieldFlag.isHidden) && (
                                        <Form.Item
                                            style={{width: 555}}
                                            name="headerDatasourceValue"
                                            label="Значение"
                                        >
                                            <CustomSelect
                                                handleTriggerEvent={(value, event, entries) => {
                                                    if (event === ECustomSelectValueTriggerEvent.firstOptionWasSet) {
                                                        if (value) applyFieldWatchers();
                                                    }
                                                    if (event === ECustomSelectValueTriggerEvent
                                                        .receivedDataFromFetcher) {
                                                        if (shouldSetFirstValueToHeaderValueRef.current) {
                                                            shouldSetFirstValueToHeaderValueRef.current = false;
                                                            if (entries?.length) {
                                                                const [{value: firstEntryValue}] = entries;
                                                                const {lookupCode} = firstEntryValue ?? {};
                                                                setFieldsValueUntouched(form, {
                                                                    headerDatasourceValue: lookupCode,
                                                                });
                                                            }
                                                        }
                                                    }
                                                }}
                                                settings={{
                                                    showSearch: true,
                                                    isDisabled: checkWatcherFlag(
                                                        'headerDatasourceValue', EWatcherFieldFlag.isLoading,
                                                    ) || checkWatcherFlag(
                                                        'headerDatasourceValue', EWatcherFieldFlag.isDisabled,
                                                    ),
                                                    isLoading: checkWatcherFlag(
                                                        'headerDatasourceValue', EWatcherFieldFlag.isLoading,
                                                    ),
                                                    url: valuesReferenceUrls.headerDatasourceValue,
                                                    placeholder: 'Выберите значение',
                                                    labelPath: 'meaning',
                                                    valuePath: 'lookupCode',
                                                    formFieldKey: 'headerDatasourceValue',
                                                    formInstance: form,
                                                    useFirstOptionAfterReceivedFromFetcher: true,
                                                    noUseFirstOptionWhenValueIsSet: true,
                                                    allowTriggerWhenNoValueFound: true,
                                                }}
                                            />
                                        </Form.Item>
                                    )}

                                    {!checkWatcherFlag('headerDatasourceSqlValue', EWatcherFieldFlag.isHidden) && (
                                        <Form.Item
                                            style={{width: 555}}
                                            name="headerDatasourceSqlValue"
                                            label="Динамический SQL"
                                        >
                                            <LargeStringInput
                                                saveButtonLabel="Подтвердить"
                                                disabled={checkWatcherFlag('headerDatasourceSqlValue',
                                                    EWatcherFieldFlag.isDisabled)}
                                                name="headerDatasourceSqlValue"
                                                label="Динамический SQL"
                                                placeholder="Динамический SQL"
                                                isDynamicSqlEnabled={isDynamicSqlEnabled}
                                            />
                                        </Form.Item>
                                    )}
                                </div>

                                <div className={cn('report-configurator__section__body__row')}>
                                    <Form.Item
                                        name="enabledColumnTitleOrdinals"
                                        valuePropName="checked"
                                    >
                                        <Checkbox disabled={checkWatcherFlag('enabledColumnTitleOrdinals',
                                            EWatcherFieldFlag.isDisabled)}
                                        >
                                            Вывод шапки отчёта с порядковым номером столбцов
                                        </Checkbox>
                                    </Form.Item>

                                    <Form.Item
                                        name="enabledComparisonBtn"
                                        valuePropName="checked"
                                    >
                                        <Checkbox disabled={checkWatcherFlag('enabledComparisonBtn',
                                            EWatcherFieldFlag.isDisabled)}
                                        >
                                            Сравнение
                                        </Checkbox>
                                    </Form.Item>

                                    <Form.Item
                                        name="enabledCrBtn"
                                        valuePropName="checked"
                                        className="with-tooltip"
                                        extra={form.getFieldValue('enabledViewControlRatioBtn') && (
                                            <FieldTooltip
                                                meta={{
                                                    contentStructure: 'Активно поле "Использовать настроенные КС", '
                                                        + 'кнопка не будет показана в отчете',
                                                } as IFieldTooltipMetadata}
                                            />
                                        )}
                                    >
                                        <Checkbox
                                            disabled={checkWatcherFlag('enabledCrBtn', EWatcherFieldFlag.isDisabled)
                                              || form.getFieldValue('enabledViewControlRatioBtn')}
                                        >
                                            Показать КС
                                        </Checkbox>
                                    </Form.Item>

                                    <Form.Item
                                        name="enabledColumnsSettingsBtn"
                                        valuePropName="checked"
                                    >
                                        <Checkbox disabled={checkWatcherFlag('enabledColumnsSettingsBtn',
                                            EWatcherFieldFlag.isDisabled)}
                                        >
                                            Настройка полей
                                        </Checkbox>
                                    </Form.Item>

                                    <Form.Item
                                        name="enabledRefreshBtn"
                                        valuePropName="checked"
                                    >
                                        <Checkbox disabled={checkWatcherFlag('enabledRefreshBtn',
                                            EWatcherFieldFlag.isDisabled)}
                                        >
                                            Кнопка &quot;Обновить&quot;
                                        </Checkbox>
                                    </Form.Item>

                                    <Form.Item
                                        name="enabledSearch"
                                        valuePropName="checked"
                                    >
                                        <Checkbox disabled={checkWatcherFlag('enabledSearch',
                                            EWatcherFieldFlag.isDisabled)}
                                        >
                                            Поиск
                                        </Checkbox>
                                    </Form.Item>

                                    <Form.Item
                                        name="enabledDownloadBtn"
                                        valuePropName="checked"
                                    >
                                        <Checkbox disabled={checkWatcherFlag('enabledDownloadBtn',
                                            EWatcherFieldFlag.isDisabled)}
                                        >
                                            Скачивание
                                        </Checkbox>
                                    </Form.Item>

                                    <Form.Item
                                        name="enabledAttachmentsBtn"
                                        valuePropName="checked"
                                        className="with-tooltip"
                                        extra={!reportConfigurationData?.withDocumentId && (
                                            <FieldTooltip
                                                meta={{
                                                    contentStructure: 'Не заполнено поле '
                                                        + '«Связь с документом»',
                                                } as IFieldTooltipMetadata}
                                            />
                                        )}
                                    >
                                        <Checkbox
                                            disabled={
                                                checkWatcherFlag('enabledAttachmentsBtn', EWatcherFieldFlag.isDisabled)
                                                || !reportConfigurationData?.withDocumentId
                                            }
                                            checked={reportConfigurationData?.enabledAttachmentsBtn}
                                        >
                                            Вложения к отчету
                                        </Checkbox>
                                    </Form.Item>

                                    <Form.Item
                                        name="actionsEnabled"
                                        valuePropName="checked"
                                    >
                                        <Checkbox
                                            disabled={checkWatcherFlag('actionsEnabled',
                                                EWatcherFieldFlag.isDisabled)}
                                            checked={reportConfigurationData?.actionsEnabled}
                                        >
                                            Действия
                                        </Checkbox>
                                    </Form.Item>

                                    <Form.Item
                                        name="enabledGroupingSettingsBtn"
                                        valuePropName="checked"
                                    >
                                        <Checkbox
                                            disabled={checkWatcherFlag('enabledGroupingSettingsBtn',
                                                EWatcherFieldFlag.isDisabled)}
                                        >
                                            Кнопка &quot;Настройка группировки&quot;
                                        </Checkbox>
                                    </Form.Item>

                                    <Form.Item
                                        name="enabledSelectAllRowsBtn"
                                        valuePropName="checked"
                                    >
                                        <Checkbox disabled={checkWatcherFlag('enabledSelectAllRowsBtn',
                                            EWatcherFieldFlag.isDisabled)}
                                        >
                                            Кнопка &quot;Выделить все&quot;
                                        </Checkbox>
                                    </Form.Item>

                                    {isViewModeSwitchSupported(chosenDesign) && (
                                        <Form.Item
                                            name="enabledDetailedSummaryBtn"
                                            valuePropName="checked"
                                        >
                                            <Checkbox disabled={checkWatcherFlag('enabledDetailedSummaryBtn',
                                                EWatcherFieldFlag.isDisabled)}
                                            >
                                                Смена режима
                                            </Checkbox>
                                        </Form.Item>
                                    )}

                                    <Form.Item
                                        name="enabledMenuWhenUsingDrillFilter"
                                        valuePropName="checked"
                                    >
                                        <Checkbox disabled={checkWatcherFlag(
                                            'enabledMenuWhenUsingDrillFilter', EWatcherFieldFlag.isDisabled,
                                        )}
                                        >
                                            Кнопка &quot;Сбросить ДД&quot;
                                        </Checkbox>
                                    </Form.Item>

                                    <Form.Item
                                        name="enabledRecordsInfo"
                                        valuePropName="checked"
                                    >
                                        <Checkbox disabled={checkWatcherFlag('enabledRecordsInfo',
                                            EWatcherFieldFlag.isDisabled)}
                                        >
                                            Показать количество строк
                                        </Checkbox>
                                    </Form.Item>
                                </div>

                                {!isCreatingNewTemplate && (
                                    <footer className={cn('report-configurator__section__body__row')}>
                                        <ClickConfirmation
                                            skip={getLeaveTabConfirmationSkipCondition}
                                            labels={switchTabConfirmationLabels}
                                            mainTextCenter
                                        >
                                            <Button
                                                disabled={isCreatingNewTemplate}
                                                type="primary"
                                                onClick={() => {
                                                    setSelectedTabKey?.(
                                                        EReportConfigurationTabKey.REPORT_HEADER_SETTINGS,
                                                    );
                                                }}
                                            >
                                                Настройка заголовка отчёта
                                            </Button>
                                        </ClickConfirmation>

                                        <ModalReportDownloadSettings
                                            isDisabled={
                                                !form.getFieldValue('enabledDownloadBtn')
                                            || checkWatcherFlag('buttonEnabledDownloadBtn',
                                                EWatcherFieldFlag.isDisabled)
                                            }
                                        />

                                        <ModalReportActionsSettings
                                            templateCode={templateCode}
                                            isDisabled={
                                                checkWatcherFlag('buttonActionsEnabled', EWatcherFieldFlag.isDisabled)
                                            }
                                        />
                                    </footer>
                                )}
                            </div>
                        </article>

                        <article className={cn('report-configurator__section')}>
                            <h3 className={cn('report-configurator__section__title')}>
                                Источник данных табличной части отчета
                            </h3>

                            <div className={cn('report-configurator__section__body')}>
                                <div className={cn(
                                    'report-configurator__section__body__row',
                                )}
                                >
                                    <Form.Item
                                        style={{width: 555}}
                                        name="tableDatasourceType"
                                        label="Тип источника"
                                    >
                                        <CustomSelect
                                            settings={{
                                                showSearch: true,
                                                isDisabled: checkWatcherFlag(
                                                    'tableDatasourceType', EWatcherFieldFlag.isDisabled,
                                                ),
                                                placeholder: 'Выберите тип',
                                                url: '/lookupValue/TABLE_REPORT_SOURCE_TYPE',
                                                labelPath: 'meaning',
                                                valuePath: 'lookupCode',
                                                formFieldKey: 'tableDatasourceType',
                                                depsToCallCustomTrigger: [shouldUpdateSelectUrlsTrigger],
                                            }}
                                            handleTriggerEvent={(val, event) => {
                                                if (val?.lookupCode && val?.attribute1) {
                                                    const fetchUrl = `${reportConfiguratorFetchMapping[
                                                        val.lookupCode]}/${val.attribute1}`;
                                                    setValuesReferenceUrls(p => {
                                                        if (p.tableDatasourceValue === fetchUrl) return p;
                                                        if (event === ECustomSelectValueTriggerEvent
                                                            .valueHasBeenChangedManually) {
                                                            shouldSetFirstValueToTableValueRef.current = true;
                                                        }
                                                        return {
                                                            ...p,
                                                            tableDatasourceValue: fetchUrl,
                                                        };
                                                    });
                                                }
                                            }}
                                        />
                                    </Form.Item>

                                    {!checkWatcherFlag('tableDatasourceValue', EWatcherFieldFlag.isHidden) && (
                                        <Form.Item
                                            style={{width: 555}}
                                            name="tableDatasourceValue"
                                            label="Значение"
                                        >
                                            <CustomSelect
                                                handleTriggerEvent={(value, event, entries) => {
                                                    if (event === ECustomSelectValueTriggerEvent.firstOptionWasSet) {
                                                        if (value) applyFieldWatchers();
                                                    }
                                                    if (event === ECustomSelectValueTriggerEvent
                                                        .receivedDataFromFetcher) {
                                                        if (shouldSetFirstValueToTableValueRef.current) {
                                                            shouldSetFirstValueToTableValueRef.current = false;
                                                            if (entries?.length) {
                                                                const [{value: firstEntryValue}] = entries;
                                                                const {lookupCode} = firstEntryValue ?? {};
                                                                setFieldsValueUntouched(form, {
                                                                    tableDatasourceValue: lookupCode,
                                                                });
                                                            }
                                                        }
                                                    }
                                                }}
                                                settings={{
                                                    showSearch: true,
                                                    isDisabled: checkWatcherFlag(
                                                        'tableDatasourceValue', EWatcherFieldFlag.isLoading,
                                                    )
                                                    || checkWatcherFlag(
                                                        'tableDatasourceValue', EWatcherFieldFlag.isDisabled,
                                                    ),
                                                    isLoading: checkWatcherFlag(
                                                        'tableDatasourceValue', EWatcherFieldFlag.isLoading,
                                                    ),
                                                    url: valuesReferenceUrls.tableDatasourceValue,
                                                    placeholder: 'Выберите значение',
                                                    labelPath: 'meaning',
                                                    valuePath: 'lookupCode',
                                                    useFirstOptionAfterReceivedFromFetcher: true,
                                                    noUseFirstOptionWhenValueIsSet: true,
                                                    allowTriggerWhenNoValueFound: true,
                                                    formFieldKey: 'tableDatasourceValue',
                                                }}
                                            />
                                        </Form.Item>
                                    )}

                                    {!checkWatcherFlag('tableDatasourceSqlValue', EWatcherFieldFlag.isHidden) && (
                                        <Form.Item
                                            style={{width: 555}}
                                            name="tableDatasourceSqlValue"
                                            label="Динамический SQL"
                                        >
                                            <LargeStringInput
                                                saveButtonLabel="Подтвердить"
                                                disabled={checkWatcherFlag('tableDatasourceSqlValue',
                                                    EWatcherFieldFlag.isDisabled)}
                                                name="tableDatasourceSqlValue"
                                                label="Динамический SQL"
                                                placeholder="Динамический SQL"
                                                isDynamicSqlEnabled={isDynamicSqlEnabled}
                                            />
                                        </Form.Item>
                                    )}
                                </div>

                                <div className={cn('report-configurator__section__body__row')}>
                                    <Form.Item
                                        name="enabledRowComments"
                                        valuePropName="checked"
                                        className="with-tooltip"
                                        extra={!reportConfigurationData?.reservedColumns?.rowIdColumnName && (
                                            <FieldTooltip
                                                meta={{
                                                    contentStructure: 'Не заполнено поле '
                                                        + '«Уникальный идентификатор строки»',
                                                } as IFieldTooltipMetadata}
                                            />
                                        )}
                                    >
                                        <Checkbox disabled={
                                            checkWatcherFlag('enabledRowComments', EWatcherFieldFlag.isDisabled)
                                            || !reportConfigurationData?.reservedColumns?.rowIdColumnName
                                        }
                                        >
                                            Комментарии к строкам
                                        </Checkbox>
                                    </Form.Item>

                                    <Form.Item
                                        name="enabledRowAttachments"
                                        valuePropName="checked"
                                        className="with-tooltip"
                                        extra={!reportConfigurationData?.reservedColumns?.rowIdColumnName && (
                                            <FieldTooltip
                                                meta={{
                                                    contentStructure: 'Не заполнено поле '
                                                    + '«Уникальный идентификатор строки»',
                                                } as IFieldTooltipMetadata}
                                            />
                                        )}
                                    >
                                        <Checkbox disabled={
                                            checkWatcherFlag('enabledRowAttachments', EWatcherFieldFlag.isDisabled)
                                            || !reportConfigurationData?.reservedColumns?.rowIdColumnName
                                        }
                                        >
                                            Вложения к строкам
                                        </Checkbox>
                                    </Form.Item>

                                    <Form.Item
                                        name="enabledRowRequests"
                                        valuePropName="checked"
                                        className="with-tooltip"
                                        extra={!reportConfigurationData?.reservedColumns?.rowIdColumnName && (
                                            <FieldTooltip
                                                meta={{
                                                    contentStructure: 'Не заполнено поле '
                                                        + '«Уникальный идентификатор строки»',
                                                } as IFieldTooltipMetadata}
                                            />
                                        )}
                                    >
                                        <Checkbox disabled={
                                            checkWatcherFlag('enabledRowRequests', EWatcherFieldFlag.isDisabled)
                                            || !reportConfigurationData?.reservedColumns?.rowIdColumnName
                                        }
                                        >
                                            Создание запроса по строке
                                        </Checkbox>
                                    </Form.Item>

                                    <Form.Item
                                        name="enabledComparison"
                                        valuePropName="checked"
                                    >
                                        <Checkbox disabled={
                                            checkWatcherFlag('enabledComparison', EWatcherFieldFlag.isDisabled)
                                        }
                                        >
                                            Добавить к сравнению
                                        </Checkbox>
                                    </Form.Item>

                                    <Form.Item
                                        name="enabledViewControlRatioBtn"
                                        valuePropName="checked"
                                        className="with-tooltip"
                                        extra={(form.getFieldValue('enabledCr') || form.getFieldValue('enabledCrBtn'))
                                          && (
                                              <FieldTooltip
                                                  meta={{
                                                      contentStructure: 'Активно поле '
                                                      + '"Настройка КС"',
                                                  } as IFieldTooltipMetadata}
                                              />
                                          )}
                                    >
                                        <Checkbox
                                            disabled={checkWatcherFlag('enabledViewControlRatioBtn',
                                                EWatcherFieldFlag.isDisabled)
                                                || form.getFieldValue('enabledCr')
                                                || form.getFieldValue('enabledCrBtn')}
                                        >
                                            Использовать настроенные КС
                                        </Checkbox>
                                    </Form.Item>

                                    <Form.Item
                                        name="enabledCr"
                                        valuePropName="checked"
                                        className="with-tooltip"
                                        extra={form.getFieldValue('enabledViewControlRatioBtn') && (
                                            <FieldTooltip
                                                meta={{
                                                    contentStructure: 'Активно поле '
                                                        + '"Использовать настроенные КС"',
                                                } as IFieldTooltipMetadata}
                                            />
                                        )}
                                    >
                                        <Checkbox
                                            disabled={
                                                checkWatcherFlag('enabledCr', EWatcherFieldFlag.isDisabled)
                                            || form.getFieldValue('enabledViewControlRatioBtn')
                                            }
                                        >
                                            Настройка КС
                                        </Checkbox>
                                    </Form.Item>

                                    <Form.Item
                                        name="enabledPrint"
                                        valuePropName="checked"
                                    >
                                        <Checkbox disabled={
                                            checkWatcherFlag('enabledPrint', EWatcherFieldFlag.isDisabled)
                                        }
                                        >
                                            Печать
                                        </Checkbox>
                                    </Form.Item>

                                    <Form.Item
                                        name="enabledDrillUpBtn"
                                        valuePropName="checked"
                                    >
                                        <Checkbox disabled={checkWatcherFlag('enabledDrillUpBtn',
                                            EWatcherFieldFlag.isDisabled)}
                                        >
                                            Кнопка &quot;Drill Up&quot;
                                        </Checkbox>
                                    </Form.Item>
                                    <Form.Item
                                        name="enabledEnabledPreExecution"
                                        valuePropName="checked"
                                    >
                                        <Checkbox disabled={checkWatcherFlag('enabledEnabledPreExecution',
                                            EWatcherFieldFlag.isDisabled)}
                                        >
                                            Предзапрос данных из внешнего хранилища
                                        </Checkbox>
                                    </Form.Item>
                                </div>

                                {!isCreatingNewTemplate && (
                                    <footer className={cn('report-configurator__section__body__row')}>
                                        <ClickConfirmation
                                            skip={getLeaveTabConfirmationSkipCondition}
                                            labels={switchTabConfirmationLabels}
                                            mainTextCenter
                                        >
                                            <Button
                                                disabled={isCreatingNewTemplate}
                                                type="primary"
                                                onClick={() => {
                                                    setSelectedTabKey?.(
                                                        EReportConfigurationTabKey.REPORT_TABLE_SETTINGS,
                                                    );
                                                }}
                                            >Настройка табличной части
                                            </Button>
                                        </ClickConfirmation>

                                        <TableReservedColumnsModalOpener
                                            isSheetCodeRequired={checkWatcherFlag('paginationSheetCode',
                                                EWatcherFieldFlag.isRequired)}
                                            isDisabled={
                                                checkWatcherFlag('buttonPaginationSettings',
                                                    EWatcherFieldFlag.isDisabled)
                                            }
                                        />

                                        <ClickConfirmation
                                            skip={getLeaveTabConfirmationSkipCondition}
                                            labels={switchTabConfirmationLabels}
                                            mainTextCenter
                                        >
                                            <Button
                                                disabled={
                                                    isCreatingNewTemplate
                                            || !form.getFieldValue('enabledComparison')
                                            || checkWatcherFlag('buttonEnabledComparison', EWatcherFieldFlag.isDisabled)
                                                }
                                                type="primary"
                                                onClick={() => {
                                                    setSelectedTabKey?.(EReportConfigurationTabKey.REPORT_COMPARISON);
                                                }}
                                            >Настройка сравнения
                                            </Button>
                                        </ClickConfirmation>

                                        <ClickConfirmation
                                            skip={getLeaveTabConfirmationSkipCondition}
                                            labels={switchTabConfirmationLabels}
                                            mainTextCenter
                                        >
                                            <Button
                                                disabled={
                                                    !form.getFieldValue('enabledCr')
                                                || isCreatingNewTemplate
                                                || checkWatcherFlag('buttonEnabledCrSettings',
                                                    EWatcherFieldFlag.isDisabled)
                                                }
                                                type="primary"
                                                onClick={() => {
                                                    setSelectedTabKey?.(EReportConfigurationTabKey.REPORT_KS_SETTINGS);
                                                }}
                                            >Настройка КС
                                            </Button>
                                        </ClickConfirmation>
                                    </footer>
                                )}
                            </div>
                        </article>
                    </Form>
                </section>
            </Spin>
        </>
    );
};
